import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient, Zustand as Plattformzustand, Maschine, Systemversion } from '../api';
import { IconMaschine } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte, type Zustand,
} from '../muster';

/**
 * Zustand der Plattform.
 *
 * Aktualisiert sich alle zehn Sekunden. Das ist haeufig genug, um einen
 * laufenden Export zu verfolgen, und selten genug, um die Datenbank nicht zu
 * belasten - die Abfrage kostet mehrere Zaehlungen.
 */
export function Zustandsseite({ api }: { api: ApiClient }) {
  const [zustand, setZustand] = useState<Plattformzustand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [version, setVersion] = useState<Systemversion | null>(null);

  const laden = useCallback(async (aktiv: () => boolean) => {
    try {
      const daten = await api.zustand();
      if (aktiv()) { setZustand(daten); setFehler(null); }
    } catch (e) {
      if (aktiv()) setFehler(e);
    }
  }, [api]);

  useEffect(() => {
    let aktiv = true;
    const istAktiv = () => aktiv;

    laden(istAktiv);
    const takt = setInterval(() => laden(istAktiv), 10_000);

    return () => { aktiv = false; clearInterval(takt); };
  }, [laden]);

  // FV1: einmal je Besuch - der Stand ändert sich nur mit einem neuen Release. Scheitert die Abfrage, entfällt die
  // Fußzeile (keine leere Zeile).
  useEffect(() => {
    let aktiv = true;
    api.systemversion()
      .then((v) => { if (aktiv) setVersion(v); })
      .catch(() => { if (aktiv) setVersion(null); });
    return () => { aktiv = false; };
  }, [api]);

  const fuss = <Versionsfusszeile version={version} />;

  const spalten: Spalte<Maschine>[] = useMemo(() => [
    {
      schluessel: 'name', titel: 'Maschine', immerSichtbar: true,
      wert: (m) => m.name,
      inhalt: (m) => <strong>{m.name}</strong>,
    },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '140px',
      wert: (m) => maschinenzustand(m).text,
      inhalt: (m) => {
        const z = maschinenzustand(m);
        return <StatusBadge zustand={z.zustand} text={z.text} klein />;
      },
    },
    {
      schluessel: 'lebenszeichen', titel: 'Lebenszeichen', breite: '150px',
      wert: (m) => m.lastHeartbeat ?? '',
      inhalt: (m) => (
        <span className="schwach" title={m.lastHeartbeat ?? undefined}>
          {vorWieLange(m.lastHeartbeat)}
        </span>
      ),
    },
    {
      schluessel: 'auftrag', titel: 'Auftrag',
      wert: (m) => m.auftrag?.jobNumber ?? '',
      inhalt: (m) => (m.auftrag ? (
        <>
          {m.auftrag.jobNumber} <span className="sehr-schwach">{m.auftrag.art}</span>
          <br />
          <span className="sehr-schwach">seit {vorWieLange(m.auftrag.startedAt)}</span>
        </>
      ) : (
        <span className="sehr-schwach">—</span>
      )),
    },
    {
      schluessel: 'revit', titel: 'Revit',
      wert: (m) => m.revitVersions ?? '',
      inhalt: (m) => <span className="schwach">{m.revitVersions ?? '—'}</span>,
    },
    {
      schluessel: 'fertig', titel: 'Fertig', art: 'zahl', breite: '90px',
      wert: (m) => m.completedCount,
    },
    {
      schluessel: 'fehler', titel: 'Fehler', art: 'zahl', breite: '90px',
      wert: (m) => m.failedCount,
      inhalt: (m) => (m.failedCount > 0
        ? <StatusBadge zustand="fehler" text={`${m.failedCount}`} klein
                       titel={`${m.failedCount} fehlgeschlagene Aufträge`} />
        : '0'),
    },
  ], []);

  const kopf = (
    <PageHeader
      titel="Zustand der Plattform"
      beschreibung={'Warteschlange, Verarbeitungsmaschinen und die Bilanz der '
        + 'letzten 24 Stunden.'}
      meta={zustand && (
        <>
          Stand {new Date(zustand.zeitpunkt).toLocaleTimeString('de-DE')}
          {' · '}aktualisiert sich alle 10 s
        </>
      )}
    />
  );

  if (fehler && !zustand) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Der Zustand der Plattform konnte nicht abgefragt werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); laden(() => true); }}
        />
        {fuss}
      </>
    );
  }

  if (!zustand) {
    return (
      <>
        {kopf}
        <LoadingState text="Zustand der Plattform wird geladen …" zeilen={5} spalten={5} />
        {fuss}
      </>
    );
  }

  const erreichbar = zustand.maschinen.filter((m) => m.erreichbar).length;

  return (
    <>
      {kopf}

      {zustand.warnungen.map((w, i) => (
        <div key={i} className={`hinweis ${w.stufe === 'fehler' ? 'fehler' : 'warnung'}`}>
          <span className="hinweis-zeichen">!</span>
          {w.text}
        </div>
      ))}

      <KpiReihe>
        <KpiCard
          titel="Maschinen erreichbar"
          wert={`${erreichbar} / ${zustand.maschinen.length}`}
          // Sind gar keine Maschinen registriert, meldet sich auch keine —
          // das ist kein Fehler, sondern ein leerer Bestand. Rot bleibt dem
          // tatsächlichen Problem vorbehalten (Abschnitt 18).
          zustand={zustand.maschinen.length > 0 && erreichbar === 0
            ? 'fehler' : undefined}
          zustandText={zustand.maschinen.length > 0 && erreichbar === 0
            ? 'keine Maschine meldet sich' : undefined}
        />
        <KpiCard
          titel="Wartend"
          wert={zustand.warteschlange.wartend}
          zustand={zustand.warteschlange.wartend > 0 && erreichbar === 0 ? 'fehler' : undefined}
          zustandText={zustand.warteschlange.wartend > 0 && erreichbar === 0
            ? 'wartet ohne Maschine' : undefined}
        />
        <KpiCard titel="In Arbeit" wert={zustand.warteschlange.inArbeit} />
        <KpiCard
          titel="Fertig"
          wert={zustand.auftraege24h.erfolgreich}
          vergleich="letzte 24 Stunden"
        />
        <KpiCard
          titel="Fehlgeschlagen"
          wert={zustand.auftraege24h.fehlgeschlagen}
          vergleich="letzte 24 Stunden"
          zustand={zustand.auftraege24h.fehlgeschlagen > 0 ? 'fehler' : undefined}
          zustandText={zustand.auftraege24h.fehlgeschlagen > 0 ? 'nachsehen' : undefined}
        />
      </KpiReihe>

      <DataTable
        name="Verarbeitungsmaschinen"
        spalten={spalten}
        zeilen={zustand.maschinen}
        schluessel={(m) => m.id}
        fehler={fehler}
        beiErneutVersuchen={() => { setFehler(null); laden(() => true); }}
        suche={zustand.maschinen.length > 8}
        leer={(
          <EmptyState
            icon={<IconMaschine />}
            titel="Keine Maschine registriert."
            erklaerung={'Eine Maschine erscheint hier, sobald der Agent auf ihr '
              + 'läuft und sich das erste Mal meldet.'}
          />
        )}
      />
      {fuss}
    </>
  );
}

/**
 * FV1 (Eigentümer 21.10.2026): Fußzeile der Startseite mit dem laufenden Stand - Releasename, Bauzeit in UTC und
 * kurzer Commit, zum Beispiel „TGAPlan v0.52.29-… · Build 21.10.2026 14:13 UTC · 832e1877“. Ohne gesetzten Bau
 * „TGAPlan Entwicklungsstand“. Die Werte kommen nur über die angemeldete Route `GET /api/system/version`, nie aus dem
 * Web-Bündel; die Seite selbst steht hinter der Anmeldung.
 */
export function Versionsfusszeile({ version }: { version: Systemversion | null }) {
  if (!version) return null;
  return (
    <footer className="sehr-schwach" data-testid="versionsfusszeile"
            style={{ marginTop: 24, fontSize: 12, textAlign: 'center' }}>
      {versionstext(version)}
    </footer>
  );
}

/** Der Text der Fußzeile (für sich prüfbar). */
export function versionstext(version: Systemversion): string {
  if (!version.gesetzt) return 'TGAPlan Entwicklungsstand';
  const teile = [`TGAPlan ${version.release}`];
  if (version.bauzeitUtc) teile.push(`Build ${bauzeitUtc(version.bauzeitUtc)} UTC`);
  if (version.commit) teile.push(version.commit.slice(0, 8));
  return teile.join(' · ');
}

/** „21.10.2026 14:13“ - immer in UTC, nie in der Ortszeit des Browsers. */
export function bauzeitUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const z = (n: number) => String(n).padStart(2, '0');
  return `${z(d.getUTCDate())}.${z(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} `
    + `${z(d.getUTCHours())}:${z(d.getUTCMinutes())}`;
}

/**
 * Der gemeldete Zustand gilt nur, solange sich die Maschine meldet. Stuerzt
 * der Agent ab, bleibt er auf "arbeitet" stehen - deshalb hat das
 * Lebenszeichen Vorrang.
 */
function maschinenzustand(maschine: Maschine): { text: string; zustand: Zustand } {
  if (!maschine.erreichbar) return { text: 'offline', zustand: 'fehler' };
  if (maschine.gemeldeterZustand === 2) return { text: 'arbeitet', zustand: 'info' };
  if (maschine.gemeldeterZustand === 3) return { text: 'deaktiviert', zustand: 'neutral' };
  return { text: 'bereit', zustand: 'gut' };
}

/** Zeitangabe in der Form, wie man sie im Gespraech verwenden wuerde. */
export function vorWieLange(zeitpunkt: string | null): string {
  if (!zeitpunkt) return 'nie';

  const sekunden = Math.floor((Date.now() - new Date(zeitpunkt).getTime()) / 1000);

  if (sekunden < 60) return `vor ${sekunden} s`;
  if (sekunden < 3600) return `vor ${Math.floor(sekunden / 60)} min`;
  if (sekunden < 86400) return `vor ${Math.floor(sekunden / 3600)} h`;
  return `vor ${Math.floor(sekunden / 86400)} Tagen`;
}
